import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { SnackbarService } from './snackbar.service';

export type StopwatchState = 'IDLE' | 'RUNNING' | 'CHECKED_OUT' | 'SYNCING';

export interface TimerSession {
  state: StopwatchState;
  checkInTime: Date | null;
  accumulatedSeconds: number;
  isCheckedIn: boolean;
  hasWorkedToday: boolean;
  attendanceId: string | null;
}

@Injectable({ providedIn: 'root' })
export class AttendanceTimerService implements OnDestroy {

  private _session: TimerSession = {
    state: 'IDLE',
    checkInTime: null,
    accumulatedSeconds: 0,
    isCheckedIn: false,
    hasWorkedToday: false,
    attendanceId: null
  };

  readonly display$ = new BehaviorSubject<string>('00:00:00');
  readonly session$ = new BehaviorSubject<TimerSession>({ ...this._session });

  private tickInterval: any  = null;
  private pollInterval: any  = null;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private snackbar: SnackbarService
  ) {}


  startSession(attendanceId: string, checkInTime: Date, previousAccumulatedSeconds = 0): void {
    this._session = {
      state: 'RUNNING',
      checkInTime,
      accumulatedSeconds: previousAccumulatedSeconds,
      isCheckedIn: true,
      hasWorkedToday: true,
      attendanceId
    };
    this.session$.next({ ...this._session });
    this._startTick();
    this._startPolling();
  }

  endSession(): void {
    const frozen = this._currentTotal();
    this._session = {
      state: 'CHECKED_OUT',
      checkInTime: null,
      accumulatedSeconds: frozen,
      isCheckedIn: false,
      hasWorkedToday: true,
      attendanceId: null
    };
    this._stopTick();
    this._stopPolling();
    this.display$.next(this._secondsToDisplay(frozen));
    this.session$.next({ ...this._session });
  }

  setSyncing(): void {
    if (this._session.state === 'IDLE') {
      this._session = { ...this._session, state: 'SYNCING' };
      this.session$.next({ ...this._session });
    }
  }

  hydrateFromRecords(records: any[]): void {
    let accumulated = 0;
    let activeRecord: any = null;

    const sorted = [...records]
      .filter(r => r.checkIn)
      .sort((a, b) =>
        this._parseTime(a.checkIn, a.date).getTime() -
        this._parseTime(b.checkIn, b.date).getTime()
      );

    for (const r of sorted) {
      if (r.checkIn && r.checkOut) {
        const diff = Math.floor(
          (this._parseTime(r.checkOut, r.date).getTime() -
           this._parseTime(r.checkIn,  r.date).getTime()) / 1000
        );
        if (diff > 0) accumulated += diff;
      } else if (r.checkIn && !r.checkOut) {
        activeRecord = r;
      }
    }

    if (activeRecord) {
      this._session = {
        state: 'RUNNING',
        checkInTime: this._parseTime(activeRecord.checkIn, activeRecord.date),
        accumulatedSeconds: accumulated,
        isCheckedIn: true,
        hasWorkedToday: true,
        attendanceId: activeRecord.id
      };
      this._startTick();
      this._startPolling();

    } else if (accumulated > 0) {
      this._session = {
        state: 'CHECKED_OUT',
        checkInTime: null,
        accumulatedSeconds: accumulated,
        isCheckedIn: false,
        hasWorkedToday: true,
        attendanceId: null
      };
      this._stopTick();
      this._stopPolling();
      this.display$.next(this._secondsToDisplay(accumulated));

    } else {
      this._session = {
        state: 'IDLE',
        checkInTime: null,
        accumulatedSeconds: 0,
        isCheckedIn: false,
        hasWorkedToday: false,
        attendanceId: null
      };
      this._stopTick();
      this._stopPolling();
      this.display$.next('00:00:00');
    }

    this.session$.next({ ...this._session });
  }

  get currentDisplay(): string  { return this.display$.value; }
  get currentSession(): TimerSession { return { ...this._session }; }

  ngOnDestroy(): void {
    this._stopTick();
    this._stopPolling();
  }


  private _startTick(): void {
    if (this.tickInterval) clearInterval(this.tickInterval);
    this._tick();
    this.tickInterval = setInterval(() => this._tick(), 1000);
  }

  private _stopTick(): void {
    if (this.tickInterval) { clearInterval(this.tickInterval); this.tickInterval = null; }
  }

  private _tick(): void {
    this.display$.next(this._secondsToDisplay(this._currentTotal()));
  }

  private _currentTotal(): number {
    if (!this._session.checkInTime) return this._session.accumulatedSeconds;
    const elapsed = Math.max(
      0,
      Math.floor((Date.now() - this._session.checkInTime.getTime()) / 1000)
    );
    return this._session.accumulatedSeconds + elapsed;
  }


  private _secondsToDisplay(total: number): string {
    const s   = Math.max(0, Math.floor(total));
    const h   = Math.floor(s / 3600);
    const m   = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return [h, m, sec].map(x => String(x).padStart(2, '0')).join(':');
  }


  _parseTime(time: any, dateCtx?: any): Date {
    if (time == null) return new Date(0);

    let hh = 0, mm = 0, ss = 0;
    if (Array.isArray(time)) {
      [hh, mm, ss] = [time[0] || 0, time[1] || 0, time[2] || 0];
    } else {
      const parts = String(time).split('.')[0].split(':');
      hh = +parts[0] || 0; mm = +parts[1] || 0; ss = +parts[2] || 0;
    }

    const now = new Date();
    let y = now.getFullYear(), mo = now.getMonth(), d = now.getDate();
    if (Array.isArray(dateCtx)) {
      y = dateCtx[0]; mo = (dateCtx[1] || 1) - 1; d = dateCtx[2] || 1;
    } else if (dateCtx) {
      const p = String(dateCtx).split('T')[0].split('-');
      if (p.length === 3) { y = +p[0]; mo = (+p[1]) - 1; d = +p[2]; }
    }

    const dt = new Date(y, mo, d, hh, mm, ss, 0);
    return isNaN(dt.getTime()) ? new Date(0) : dt;
  }


  private _startPolling(): void {
    this._stopPolling();
    this.pollInterval = setInterval(() => this._poll(), 45_000);
  }

  private _stopPolling(): void {
    if (this.pollInterval) { clearInterval(this.pollInterval); this.pollInterval = null; }
  }

  private _poll(): void {
    if (this._session.state !== 'RUNNING') { this._stopPolling(); return; }

    const user = this.authService.getCurrentUser();
    if (!user?.id) return;

    const today = this._localDateStr(new Date());

    this.apiService.get(`/attendance?date=${today}`).subscribe({
      next: (records: any[]) => {
        if (this._session.state !== 'RUNNING') return;

        const mine = (records || []).filter(
          (r: any) => r.employeeId === user.id &&
                      (r.date || '').split('T')[0] === today
        );
        const stillOpen = mine.some((r: any) => r.checkIn && !r.checkOut);

        if (!stillOpen) {
          this.endSession();
          this.snackbar.showWarning('Your session was ended on another device.');
        }
      },
      error: () => {}
    });
  }

  private _localDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }
}
